<template>
  <div>
    <div class="my-3">
      <base-text-editor
        ref="text-editor__content"
        v-model="$v.forumContent.$model"
        class="quill-editor--round input__forum-thread-content"
        :class="{
          text_editor__required: contentInputState === false
        }"
        :toolbar="toolbar"
        :drop-image="false"
      ></base-text-editor>
    </div>
    <div
      ref="uploader__container"
      class="mt-4 box-media box-media--with-padding"
      :class="{
        'box-media__required': imgInputSatate === false
      }"
    >
      <b-tabs lazy pills nav-class="nav-line nav-line-alt" content-class="pt-4">
        <b-tab>
          <template slot="title">
            <span ref="text__title-image" class="font-size-md active span__post-image-tab mr-2"
              >Image (Allow multiple images)</span
            >
            <b-badge
              ref="badge__image-length"
              variant="neutral-first"
              class="text-first text__post-image"
            >
              {{ totalImages }}
            </b-badge>
            <div class="divider" />
          </template>
        </b-tab>

        <!-- upload Media -->
        <div ref="ii" class="image-upload-wrapper dropzone-image">
          <div ref="box-uploader" class="box-uploader">
            <base-s3-uploader
              id="input-upload-image"
              ref="imageUploader"
              v-model="file.images"
              class="uploader__forum"
              :reference-meta="{ ...referenceMeta }"
              :disabled="isSaving"
              :dropzone-options="dropzoneImageOptions"
              :is-public="false"
              show-cover
              hide-error-message
              @buffer:updated="onBufferUpdated"
            ></base-s3-uploader>
          </div>
        </div>
      </b-tabs>
    </div>
    <div v-if="isInvalid" class="text_feedback mb-3">
      Please fill the description or add at least 1 image to post.
    </div>

    <div class="footer mt-3">
      <b-button
        ref="button__submit"
        type="submit"
        variant="success"
        :disabled="isSaving"
        @click="submit"
        ><span v-if="!isSaving">{{ buttonTitle }}</span>

        <span v-else class="pr-2 pl-2">
          <b-spinner label="Loading..." variant="secondary" small /> </span
      ></b-button>
      <b-button
        ref="button__cancel"
        type="reset"
        variant="link"
        class="btn__back"
        @click="$emit('cancel')"
        >Cancel</b-button
      >
    </div>
  </div>
</template>

<script>
import BaseTextEditor from '@/components/base-components/BaseTextEditor';
import BaseS3Uploader from '@/components/base-components/BaseS3Uploader';
import { required } from 'vuelidate/lib/validators';
import { useValidator } from '@/composables/useValidator';
const { isNotBlank } = useValidator();

export default {
  components: {
    BaseTextEditor,
    BaseS3Uploader
  },

  props: {
    buttonTitle: {
      type: String,
      default: ''
    },
    referenceMeta: {
      type: Object,
      default: () => ({
        ref: '',
        refId: null,
        field: ''
      })
    },
    maxFilesize: {
      type: Number,
      default: 2 // MB
    },
    provider: {
      type: Function,
      default: () => {}
    },
    editedData: {
      type: Object,
      default: () => ({})
    }
  },
  validations: {
    forumContent: {
      required,
      isNotBlank
    }
  },
  data() {
    return {
      forumContent: '',
      file: {
        buffer: [],
        images: []
      },
      isLoading: false,
      isSaving: false,
      isInvalid: false,
      dropzoneImageOptions: {
        url: 'https://httpbin.org/post',
        autoProcessQueue: false,
        addRemoveLinks: true,
        acceptedFiles: '.jpeg, .jpg, .png',
        maxFilesize: this.maxFilesize,
        thumbnailHeight: 124,
        thumbnailWidth: 247
      },
      toolbar: [
        ['bold', 'italic', 'underline'],
        ['formula'],
        [{ header: '1' }, { header: '2' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['link']
      ]
    };
  },
  computed: {
    contentInputState() {
      return this.isInvalid ? !this.$v.forumContent.$invalid : null;
    },
    imgInputSatate() {
      return this.isInvalid ? this.totalImages > 0 : null;
    },
    totalImages() {
      const { buffer = [], images = [] } = this.file;

      return images.length + buffer.length;
    }
  },
  mounted() {
    if (this.editedData.content) {
      this.forumContent = this.editedData.content;
    }

    if (this.editedData.images) {
      this.$set(this.file, 'images', this.editedData.images);
    }
  },

  methods: {
    onBufferUpdated(v) {
      this.$set(this.file, 'buffer', [...v]);
    },

    async submit() {
      this.isInvalid = false;

      this.$v.forumContent.$touch();

      if (this.$v.forumContent.$invalid && this.totalImages === 0) {
        this.isInvalid = true;

        this.$emit('submit:invalid', {
          content: this.$v.forumContent.$invalid,
          images: this.totalImages === 0
        });

        return;
      }

      try {
        const body = {
          content: this.forumContent || null,
          images: this.file.images || []
        };

        this.isSaving = true;

        const entity = await this.provider(body);

        if (entity) {
          this.$set(this.referenceMeta, 'refId', entity.id);
        }

        if (this.$refs.imageUploader.hasAnyDeletedFiles()) {
          await this.$refs.imageUploader.deleteRemovedFiles();
        }

        if (this.$refs.imageUploader.hasAnyQueuedFiles()) {
          await this.$refs.imageUploader.upload();
        }

        this.$emit('submit:success', entity);
      } catch (error) {
        this.$emit('submit:error', error);
      } finally {
        this.isSaving = false;
      }
    },
    cancel() {
      return this.$emit('cancel', false);
    }
  }
};
</script>

<style lang="scss">
.uploader__forum {
  .dz-message:after {
    content: 'Drop files here to upload (2MB or less)' !important;
  }
}

.text_editor__required {
  .ql-container.ql-snow {
    background: #ffffff !important;
    border: 1px solid #f83245 !important;
    border-radius: 0 0 4.64px 4.64px !important;
  }

  .ql-toolbar.ql-snow {
    background: #ffffff !important;
    border: 1px solid #f83245 !important;
    border-radius: 4.64px 4.64px 0 0 !important;
    border-bottom: none !important;
  }
}

.text_feedback {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #f83245;
}
</style>
